import { useMutation, useQuery } from '@tanstack/react-query';
import { portalGet, portalPost } from '@/lib/portal/customerClient';
import { usePortalAuthStore, type PortalSession } from '@/stores/portalAuth.store';

export interface PortalIdentity {
  contact: { id: string; name: string; email: string };
  tenant: {
    id: string;
    name: string;
    slug: string;
    logoUrl?: string | null;
    phone?: string | null;
    email?: string | null;
  };
}

/**
 * Login outcome. Usually a session; a `select` result only when the same email
 * + password matches more than one company, so the customer picks one and the
 * client re-submits with that `tenantId`.
 */
export type PortalLoginResult =
  | { kind: 'session'; session: PortalSession }
  | { kind: 'select'; accounts: Array<{ tenantId: string; name: string }> };

/** Start registration by email alone — always resolves the same (anti-enum). */
export function usePortalRegister() {
  return useMutation({
    mutationFn: (email: string) => portalPost<{ ok: true }>('/api/portal/auth/register', { email }),
  });
}

export function usePortalForgotPassword() {
  return useMutation({
    mutationFn: (email: string) =>
      portalPost<{ ok: true }>('/api/portal/auth/forgot-password', { email }),
  });
}

/**
 * Log in by email + password. `tenantId` is only sent on the second step, after
 * a `select` result, to pick which company to sign into. On a `session` result
 * the store is populated; a `select` result is returned for the caller to show
 * the company picker.
 */
export function usePortalLogin() {
  const setSession = usePortalAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: (input: { email: string; password: string; tenantId?: string }) =>
      portalPost<PortalLoginResult>('/api/portal/auth/login', input),
    onSuccess: (result) => {
      if (result.kind === 'session') setSession(result.session);
    },
  });
}

/** Verify (set first password / adopt a changed email) → logged in. */
export function usePortalVerify() {
  const setSession = usePortalAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      portalPost<PortalSession>('/api/portal/auth/verify', input),
    onSuccess: (session) => setSession(session),
  });
}

export function usePortalResetPassword() {
  const setSession = usePortalAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      portalPost<PortalSession>('/api/portal/auth/reset-password', input),
    onSuccess: (session) => setSession(session),
  });
}

export function usePortalLogout() {
  const clear = usePortalAuthStore((s) => s.clear);
  return useMutation({
    mutationFn: () => portalPost<{ ok: true }>('/api/portal/auth/logout'),
    // Clear locally regardless — the server session is best-effort revoked.
    onSettled: () => clear(),
  });
}

/** The authenticated customer's identity + tenant. */
export function usePortalIdentity(enabled = true) {
  const token = usePortalAuthStore((s) => s.token);
  return useQuery({
    queryKey: ['portal', 'me', token],
    queryFn: () => portalGet<PortalIdentity>('/api/portal/auth/me'),
    enabled: enabled && Boolean(token),
    retry: false,
  });
}
